/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useContext, useEffect, useState } from "react";

import { Line } from "react-chartjs-2";

import { Chart, registerables } from "chart.js";
import moment from "moment";
import LanguageContext from "@/context/language/LanguageContext";

import { colors } from "@dotnet/telematics-design-system";

import { DATE_FORMATS, useMediaQueries } from "../../../../../../shared";
import {
  CreditsDto,
  CreditsMonthlyBalanceObjectDto,
  TooltipDataType
} from "../../../types";
import {
  attachTooltipUpdater,
  lineChartAreaBorder,
  lineChartOptions
} from "../../config";
import { useStyles } from "../../styles";
import { CreditsTooltip } from "../CreditsTooltip/CreditsTooltip";

Chart.register(...registerables);

interface CreditsLineChartProps {
  creditsData: CreditsDto | null;
}

const CreditsLineChart = ({ creditsData }: CreditsLineChartProps) => {
  const { language } = useContext(LanguageContext);

  const { toLg, to366 } = useMediaQueries();

  const [tooltipData, setTooltipData] = useState<TooltipDataType | null>(null);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const { creditsLineChartAbsoluteDiv } = useStyles({});

  useEffect(() => {
    attachTooltipUpdater(setTooltipData);
  }, [setTooltipData]);

  const shortMonthString = (monthObject: CreditsMonthlyBalanceObjectDto) => {
    return moment(monthObject.date).locale(language).format("MMM D");
  };

  const monthsArray = creditsData?.monthlyBalance.map(
    (monthObject: CreditsMonthlyBalanceObjectDto) =>
      shortMonthString(monthObject)
  );

  // uniqueMonthsArray function does not handle the case if last month is not the same as the previous month or is not unique.
  // In that case the last month will be treated as repeated one and will not be displayed in the chart.
  // This should not be the issue though, since there is an aggreement with BE,
  // that max length of the array will be 13 and the last month will always be the future month.
  const uniqueMonthsArray = creditsData?.monthlyBalance.map(
    (obj, index, array) => {
      const localeConvert = (date: string) =>
        moment(date).locale(language).format(DATE_FORMATS.MonthLong);

      const currentMonth = localeConvert(obj.date);
      // Get previous item (if any)
      const prevObj = array[index - 1];
      const prevMonth = prevObj ? localeConvert(prevObj.date) : null;

      // Show month name only if it's the first entry for that month
      return currentMonth === prevMonth ? "" : currentMonth;
    }
  );

  const monthlyData = creditsData?.monthlyBalance.map(
    monthObject => monthObject.total
  );

  const labelsObjectArr = creditsData?.monthlyBalance.map(
    (balanceObject, index) => {
      if (index === creditsData?.monthlyBalance.length - 2) {
        // get curr month object and add reserved data to it, so that it would be displayed in the tooltip
        return {
          date: balanceObject.date,
          total: balanceObject.total,
          deducted: balanceObject.deducted,
          added: balanceObject.added,
          reserved: creditsData.reserved
        };
      } else {
        // all the rest months will not display reserved data
        return balanceObject;
      }
    }
  );

  const data = {
    labels: to366 ? uniqueMonthsArray : monthsArray,
    datasets: [
      {
        data: monthlyData,
        labels: labelsObjectArr,
        fill: false,
        segment: {
          borderColor: colors.blue600
        },
        hoverBorderWidth: 3,
        pointHoverRadius: 6,
        pointHoverBackGroundColor: colors.blue600,
        pointStyle: "circle",
        pointRadius: 3.5,
        pointBorderColor: colors.blue600,
        borderCapStyle: "round",
        spanGaps: true
      }
    ]
  };

  return (
    <div
      id="credits-line-chart"
      data-testid="credits-line-chart"
      css={css({
        position: "relative",
        height: toLg ? "168px" : "174px",
        marginLeft: "-8px",
        paddingBottom: "16px"
      })}
    >
      <div
        css={css(creditsLineChartAbsoluteDiv)}
        data-testid="credits-line-chart-data"
      >
        <Line
          data={data as any}
          options={
            lineChartOptions(
              language,
              to366 ? uniqueMonthsArray : monthsArray,
              monthlyData,
              setIsHovered
            ) as any
          }
          plugins={[lineChartAreaBorder]}
        />
      </div>
      <CreditsTooltip tooltip={tooltipData} isHovered={isHovered} />
    </div>
  );
};

export default CreditsLineChart;
